import { resolveSubmissionPollingState, SubmissionStatusRecord } from '../util/submissionState';
import { shouldShowSubmissionInFlight } from '../util/submissionStatus';
import {
  INVENTORY_REQUEST_DETAIL_UI,
  INVENTORY_REQUEST_SUBMISSION_STATUS,
  SUBMISSION_POLL_GIVE_UP_CODE,
} from '../util/constants';
import {
  clearSubmissionStatuses,
  receiveSubmissionStatus,
  setSubmissionPollGeneration,
  SubmissionActionType,
} from './ducks/submission';

type PollTimer = ReturnType<typeof setTimeout>;

const MAX_CONSECUTIVE_POLL_FAILURES = 3;

type SubmissionStatusFetcher = (
  submissionId: string
) => Promise<SubmissionStatusRecord | undefined>;

export type SubmissionStatusByCaseRecord = SubmissionStatusRecord & {
  createdAt?: string | null;
  updatedAt?: string | null;
};

type SubmissionStatusByCaseFetcher = (
  caseExternalId: string
) => Promise<SubmissionStatusByCaseRecord | undefined>;

type SubmissionPollingDependencies = {
  dispatch: (action: SubmissionActionType) => void;
  fetchSubmissionStatus: SubmissionStatusFetcher;
  fetchSubmissionStatusByCase?: SubmissionStatusByCaseFetcher;
  pollDelayMs: number;
  schedule: (callback: () => void, delayMs: number) => PollTimer;
  cancel: (timer: PollTimer) => void;
};

type ActiveSubmissionPoll = {
  submissionId: string;
  pollGeneration: number;
  timer: PollTimer | null;
  consecutiveFailureCount: number;
};

export type SubmissionPollingConfiguration = {
  dispatch: (action: SubmissionActionType) => void;
  fetchSubmissionStatus: SubmissionStatusFetcher;
  fetchSubmissionStatusByCase?: SubmissionStatusByCaseFetcher;
  pollDelayMs: number;
};

export class SubmissionPollingOrchestrator {
  private activePolls = new Map<string, ActiveSubmissionPoll>();

  private pollGenerations = new Map<string, number>();

  // Cases this app stopped checking after repeated lookup failures, with the submission each one
  // was following, so a later return to the record or to the page can check them again.
  private givenUpPolls = new Map<string, string>();

  private dependencies: SubmissionPollingDependencies | null = null;

  configure(configuration: SubmissionPollingConfiguration): void {
    this.stopAll();
    this.givenUpPolls.clear();
    this.dependencies = {
      ...configuration,
      // Browsers throw "Illegal invocation" when the timers are called as methods of another object.
      schedule: (callback, delayMs) => setTimeout(callback, delayMs),
      cancel: timer => clearTimeout(timer),
    };
  }

  startPolling(
    caseExternalId: string,
    submissionId: string,
    requestedPollGeneration = 0,
    replaceActivePoll = false
  ): number | null {
    if (!this.dependencies || !caseExternalId || !submissionId) {
      return null;
    }

    const activePoll = this.activePolls.get(caseExternalId);
    if (activePoll) {
      if (activePoll.submissionId !== submissionId && !replaceActivePoll) {
        return null;
      }
      this.stopPolling(caseExternalId);
    }
    this.givenUpPolls.delete(caseExternalId);

    const pollGeneration = Math.max(
      (this.pollGenerations.get(caseExternalId) || 0) + 1,
      requestedPollGeneration
    );
    this.pollGenerations.set(caseExternalId, pollGeneration);
    this.dependencies.dispatch(
      setSubmissionPollGeneration(caseExternalId, pollGeneration, submissionId)
    );
    this.activePolls.set(caseExternalId, {
      submissionId,
      pollGeneration,
      timer: null,
      consecutiveFailureCount: 0,
    });
    this.schedulePoll(caseExternalId, submissionId, pollGeneration);
    return pollGeneration;
  }

  restartPolling(
    caseExternalId: string,
    submissionId: string,
    requestedPollGeneration = 0,
    replaceActivePoll = false
  ): number | null {
    return this.startPolling(
      caseExternalId,
      submissionId,
      requestedPollGeneration,
      replaceActivePoll
    );
  }

  hydrateSubmissionStatus(caseExternalId: string, statusRecord: SubmissionStatusRecord): void {
    if (!caseExternalId || !this.dependencies) {
      return;
    }

    const activePoll = this.activePolls.get(caseExternalId);
    if (activePoll && activePoll.submissionId !== statusRecord.submissionId) {
      return;
    }

    const resolution = resolveSubmissionPollingState(statusRecord);
    if (resolution.shouldContinuePolling && statusRecord.submissionId) {
      if (activePoll) {
        this.dependencies.dispatch(
          receiveSubmissionStatus(caseExternalId, statusRecord, activePoll.pollGeneration)
        );
        return;
      }

      const pollGeneration = this.startPolling(caseExternalId, statusRecord.submissionId);
      if (pollGeneration !== null) {
        this.dependencies.dispatch(
          receiveSubmissionStatus(caseExternalId, statusRecord, pollGeneration)
        );
      }
      return;
    }

    this.stopPolling(caseExternalId);
    this.givenUpPolls.delete(caseExternalId);
    this.dependencies.dispatch(
      receiveSubmissionStatus(
        caseExternalId,
        statusRecord,
        this.pollGenerations.get(caseExternalId) || 0
      )
    );
  }

  stopPolling(caseExternalId: string): void {
    const activePoll = this.activePolls.get(caseExternalId);
    if (!activePoll) {
      return;
    }

    if (activePoll.timer) {
      this.dependencies?.cancel(activePoll.timer);
    }
    this.activePolls.delete(caseExternalId);

    const nextPollGeneration = activePoll.pollGeneration + 1;
    this.pollGenerations.set(caseExternalId, nextPollGeneration);
    this.dependencies?.dispatch(setSubmissionPollGeneration(caseExternalId, nextPollGeneration));
  }

  stopAll(): void {
    Array.from(this.activePolls.keys()).forEach(caseExternalId => this.stopPolling(caseExternalId));
  }

  clearAll(): void {
    this.stopAll();
    this.givenUpPolls.clear();
    this.dependencies?.dispatch(clearSubmissionStatuses());
  }

  isPolling(caseExternalId: string): boolean {
    return this.activePolls.has(caseExternalId);
  }

  /**
   * Checks a case again after this app gave up on it (see giveUpPolling), with a fresh failure
   * budget. Does nothing unless the case was given up; starting any check clears the give-up, so a
   * given-up case is never being checked already. Returns whether checking resumed.
   */
  resumeAfterGiveUp(caseExternalId: string): boolean {
    const submissionId = this.givenUpPolls.get(caseExternalId);
    if (!submissionId) {
      return false;
    }
    return this.restartPolling(caseExternalId, submissionId) !== null;
  }

  resumeAllAfterGiveUp(): void {
    Array.from(this.givenUpPolls.keys()).forEach(caseExternalId => {
      this.resumeAfterGiveUp(caseExternalId);
    });
  }

  /**
   * Follows the submission the backend has in flight for a case when the submit reply could not be
   * followed: an accepted reply without a submission id, or a refusal because another submission is
   * already in flight. Like the lookup a screen runs on load, only a recent in-flight submission is
   * followed.
   */
  async followSubmissionByCase(caseExternalId: string): Promise<void> {
    const fetchSubmissionStatusByCase = this.dependencies?.fetchSubmissionStatusByCase;
    if (!caseExternalId || !fetchSubmissionStatusByCase || this.isPolling(caseExternalId)) {
      return;
    }

    try {
      const byCaseStatus = await fetchSubmissionStatusByCase(caseExternalId);
      const isRecentInFlight = shouldShowSubmissionInFlight(
        byCaseStatus,
        INVENTORY_REQUEST_DETAIL_UI.SUBMISSION_RECOVERY_MAX_AGE_MS
      );
      if (byCaseStatus?.submissionId && isRecentInFlight) {
        this.hydrateSubmissionStatus(caseExternalId, byCaseStatus);
      }
    } catch (_error) {
      // The screen's own lookup picks the submission up the next time the record loads.
    }
  }

  private schedulePoll(
    caseExternalId: string,
    submissionId: string,
    pollGeneration: number,
    delayMs?: number
  ): void {
    const activePoll = this.activePolls.get(caseExternalId);
    if (!activePoll || activePoll.pollGeneration !== pollGeneration || !this.dependencies) {
      return;
    }

    activePoll.timer = this.dependencies.schedule(
      () => this.poll(caseExternalId, submissionId, pollGeneration).catch(() => undefined),
      delayMs ?? this.dependencies.pollDelayMs
    );
  }

  private async poll(
    caseExternalId: string,
    submissionId: string,
    pollGeneration: number
  ): Promise<void> {
    try {
      const statusRecord = await this.dependencies?.fetchSubmissionStatus(submissionId);
      if (!this.isCurrentPoll(caseExternalId, pollGeneration) || !this.dependencies) {
        return;
      }

      if (!statusRecord) {
        this.handleTransientPollFailure(caseExternalId, submissionId, pollGeneration);
        return;
      }

      const activePoll = this.activePolls.get(caseExternalId);
      if (activePoll) {
        activePoll.consecutiveFailureCount = 0;
      }

      this.dependencies.dispatch(
        receiveSubmissionStatus(caseExternalId, statusRecord, pollGeneration)
      );
      if (resolveSubmissionPollingState(statusRecord).shouldContinuePolling) {
        this.schedulePoll(
          caseExternalId,
          statusRecord.submissionId || submissionId,
          pollGeneration
        );
        return;
      }

      this.stopPolling(caseExternalId);
    } catch (_error) {
      if (this.isCurrentPoll(caseExternalId, pollGeneration)) {
        this.handleTransientPollFailure(caseExternalId, submissionId, pollGeneration);
      }
    }
  }

  private handleTransientPollFailure(
    caseExternalId: string,
    submissionId: string,
    pollGeneration: number
  ): void {
    const activePoll = this.activePolls.get(caseExternalId);
    if (!activePoll || !this.dependencies) {
      return;
    }

    activePoll.consecutiveFailureCount += 1;
    if (activePoll.consecutiveFailureCount < MAX_CONSECUTIVE_POLL_FAILURES) {
      const backoffDelayMs =
        this.dependencies.pollDelayMs * 2 ** activePoll.consecutiveFailureCount;
      this.schedulePoll(caseExternalId, submissionId, pollGeneration, backoffDelayMs);
      return;
    }

    this.giveUpPolling(caseExternalId, submissionId, pollGeneration);
  }

  /**
   * Stops checking a case after repeated failed lookups. A lookup that failed says nothing about
   * the submission, so it is recorded as still in flight rather than failed, and the case is checked
   * again when the user comes back to it (see resumeAfterGiveUp).
   */
  private giveUpPolling(
    caseExternalId: string,
    submissionId: string,
    pollGeneration: number
  ): void {
    this.dependencies?.dispatch(
      receiveSubmissionStatus(
        caseExternalId,
        {
          submissionId,
          status: INVENTORY_REQUEST_SUBMISSION_STATUS.ERROR,
          deadLettered: false,
          errorCode: SUBMISSION_POLL_GIVE_UP_CODE.LOOKUP_FAILED,
        },
        pollGeneration
      )
    );
    this.stopPolling(caseExternalId);
    this.givenUpPolls.set(caseExternalId, submissionId);
  }

  private isCurrentPoll(caseExternalId: string, pollGeneration: number): boolean {
    return this.activePolls.get(caseExternalId)?.pollGeneration === pollGeneration;
  }
}

export const submissionPolling = new SubmissionPollingOrchestrator();
